const Section = (props: {title: string, children: React.ReactNode}) => (
    <section className="flex flex-col gap-3">
        <h2 className="text-xl font-semibold">{props.title}</h2>
        {props.children}
    </section>
);

const BulletList = (props: {items: string[]}) => (
    <ul className="list-disc space-y-2 pl-6">
        {props.items.map((item) => (
            <li key={item}>{item}</li>
        ))}
    </ul>
);

export const PrivacyPolicy = () => {
    return (
        <main className="min-h-screen bg-white text-black">
            <div className="mx-auto flex max-w-3xl flex-col gap-6 px-6 py-10">
                <header className="border-b border-gray-200 pb-6">
                    <h1 className="text-3xl font-bold text-maroon">OrderInOne Privacy Policy</h1>
                    <p className="mt-2 text-sm text-gray-600">Effective Date: 2 July 2026</p>
                </header>

                <p>
                    This Privacy Policy explains how Popbox Pty Ltd ("Popbox", "we", "our", or "us") collects,
                    uses, stores, and protects your information when you use the OrderInOne mobile application
                    ("the App").
                </p>
                <p>
                    By downloading, accessing, or using OrderInOne, you agree to the collection and use of your
                    information as described in this Privacy Policy.
                </p>

                <Section title="1. Company Information">
                    <p>Company Name: Popbox Pty Ltd</p>
                    <p>Email: jan@popboxtech.com</p>
                    <p>Country of Operation: South Africa</p>
                </Section>

                <Section title="2. Information We Collect">
                    <p>We collect information necessary to provide and improve our services.</p>

                    <h3 className="text-lg font-medium">Business Account Information</h3>
                    <p>When a golf course or business registers with OrderInOne, we collect:</p>
                    <BulletList items={[
                        'Email address',
                        'Encrypted password',
                        'Business name',
                        'Course logo'
                    ]} />
                    <p>This information is used to create and manage your business account.</p>

                    <h3 className="text-lg font-medium">Customer Information</h3>
                    <p>When customers place orders through the application, we collect:</p>
                    <BulletList items={['Full name']} />
                    <p>This information is used solely to identify customers and ensure orders are fulfilled accurately.</p>

                    <h3 className="text-lg font-medium">Order Information</h3>
                    <p>When an order is placed, we may collect:</p>
                    <BulletList items={[
                        'Ordered items',
                        'Order value',
                        'Date and time of the order',
                        'Order status',
                        'Golf course information',
                        'Delivery or collection location within the golf course'
                    ]} />

                    <h3 className="text-lg font-medium">Automatically Collected Information</h3>
                    <p>When using the application, certain technical information may be collected automatically, including:</p>
                    <BulletList items={[
                        'Device model',
                        'Operating system version',
                        'Application version',
                        'Device identifiers',
                        'Crash reports',
                        'Performance metrics',
                        'Anonymous usage statistics'
                    ]} />
                    <p>This information helps us maintain, improve, and secure the application.</p>
                </Section>

                <Section title="3. How We Use Your Information">
                    <p>We use your information to:</p>
                    <BulletList items={[
                        'Create and manage user accounts',
                        'Authenticate users securely',
                        'Process food and beverage orders',
                        'Deliver orders accurately',
                        'Improve application functionality',
                        'Monitor application performance',
                        'Diagnose and resolve technical issues',
                        'Provide customer support',
                        'Maintain the security of our services'
                    ]} />
                    <p>We do not sell your personal information.</p>
                </Section>

                <Section title="4. Third-Party Services">
                    <p>To operate OrderInOne, we use trusted third-party service providers.</p>

                    <h3 className="text-lg font-medium">Google Firebase</h3>
                    <p>Google Firebase provides:</p>
                    <BulletList items={[
                        'User Authentication',
                        'Cloud Firestore database',
                        'Cloud Storage',
                        'Secure cloud infrastructure'
                    ]} />

                    <h3 className="text-lg font-medium">Google Analytics</h3>
                    <p>
                        Google Analytics helps us understand how users interact with the application by collecting
                        anonymous usage information such as:
                    </p>
                    <BulletList items={[
                        'Screens viewed',
                        'Session duration',
                        'Device information',
                        'Feature usage'
                    ]} />
                    <p>This information helps improve the user experience.</p>

                    <h3 className="text-lg font-medium">Google Crashlytics</h3>
                    <p>Google Crashlytics automatically reports application crashes and technical issues.</p>
                    <p>Crash reports may include:</p>
                    <BulletList items={[
                        'Device model',
                        'Android version',
                        'Application version',
                        'Error logs',
                        'Stack traces'
                    ]} />
                    <p>Crash reports are used solely to improve application stability and reliability.</p>
                </Section>

                <Section title="5. Permissions">
                    <p>OrderInOne requests only the permissions necessary to operate the application.</p>

                    <h3 className="text-lg font-medium">Internet Access</h3>
                    <p>Internet access is required to:</p>
                    <BulletList items={[
                        'Authenticate users',
                        'Retrieve menus',
                        'Submit orders',
                        'Synchronize information',
                        'Receive application updates'
                    ]} />

                    <h3 className="text-lg font-medium">Storage Access</h3>
                    <p>Storage access is used to:</p>
                    <BulletList items={[
                        'Upload course logos',
                        'Cache application resources',
                        'Improve application performance'
                    ]} />
                    <p>OrderInOne does not access unrelated files stored on your device.</p>
                </Section>

                <Section title="6. Data Retention">
                    <p>
                        We retain personal information only for as long as necessary to provide our services or comply
                        with applicable legal obligations.
                    </p>

                    <h3 className="text-lg font-medium">Customer Profiles</h3>
                    <p>Customer profiles may be deleted upon request.</p>
                    <p>Requests can be submitted to:</p>
                    <p>jan@popboxtech.com</p>
                    <p>
                        Once verified, personal profile information will be removed unless retention is required for
                        legal or security purposes.
                    </p>

                    <h3 className="text-lg font-medium">Order Records</h3>
                    <p>Order history is retained indefinitely for:</p>
                    <BulletList items={[
                        'Historical reporting',
                        'Business analytics',
                        'Operational records',
                        'Customer support',
                        'Audit purposes'
                    ]} />
                </Section>

                <Section title="7. Data Sharing">
                    <p>
                        Popbox Pty Ltd does not sell, rent, or share your personal information with third parties for
                        advertising or marketing purposes.
                    </p>
                    <p>Your information is only accessible by:</p>
                    <BulletList items={[
                        'Authorized employees of the golf course managing your orders',
                        'Authorized Popbox administrators responsible for operating the platform',
                        'Trusted cloud service providers required to deliver the service, such as Google Firebase'
                    ]} />
                </Section>

                <Section title="8. Data Security">
                    <p>We take reasonable technical and organizational measures to safeguard your information.</p>
                    <p>These measures include:</p>
                    <BulletList items={[
                        'Secure authentication',
                        'Encrypted data transmission (HTTPS/TLS)',
                        'Access controls',
                        'Secure cloud hosting',
                        'Routine security updates'
                    ]} />
                    <p>
                        While we take appropriate precautions, no method of electronic transmission or storage is
                        completely secure. We therefore cannot guarantee absolute security.
                    </p>
                </Section>

                <Section title="9. Your Privacy Rights">
                    <p>Subject to applicable law, you may have the right to:</p>
                    <BulletList items={[
                        'Access your personal information',
                        'Correct inaccurate information',
                        'Request deletion of your personal information',
                        'Withdraw consent where applicable',
                        'Lodge a complaint with the appropriate data protection authority'
                    ]} />
                    <p>Requests regarding your personal information can be submitted to:</p>
                    <p>jan@popboxtech.com</p>
                </Section>

                <Section title="10. Children's Privacy">
                    <p>OrderInOne is intended for adults and is not designed for individuals under the age of 18.</p>
                    <p>We do not knowingly collect personal information from children.</p>
                    <p>
                        If we become aware that information has been collected from a child, we will delete it as soon
                        as reasonably practicable.
                    </p>
                </Section>

                <Section title="11. International Data Transfers">
                    <p>
                        Although Popbox Pty Ltd operates in South Africa, data may be processed or stored using secure
                        cloud infrastructure located in other countries through Google Cloud services.
                    </p>
                    <p>
                        By using OrderInOne, you acknowledge and consent to the transfer of your information where
                        permitted by applicable law.
                    </p>
                </Section>

                <Section title="12. Protection of Personal Information (POPIA)">
                    <p>
                        Popbox Pty Ltd is committed to complying with the Protection of Personal Information Act
                        (POPIA) of South Africa.
                    </p>
                    <p>
                        We process personal information lawfully, fairly, and transparently, and only for legitimate
                        business purposes.
                    </p>
                </Section>

                <Section title="13. Google Play User Data Compliance">
                    <p>OrderInOne complies with the Google Play User Data Policy by:</p>
                    <BulletList items={[
                        'Collecting only information necessary to provide the service.',
                        'Using secure methods to transmit and store data.',
                        'Providing users with a mechanism to request deletion of their personal information.',
                        'Not selling personal information.',
                        'Restricting access to authorized personnel and trusted infrastructure providers only.'
                    ]} />
                </Section>

                <Section title="14. Changes to This Privacy Policy">
                    <p>
                        We may update this Privacy Policy from time to time to reflect changes in our services, legal
                        requirements, or operational practices.
                    </p>
                    <p>
                        When changes are made, the updated Privacy Policy will be published within the application or
                        on our website with a revised effective date.
                    </p>
                    <p>
                        Your continued use of OrderInOne after changes become effective constitutes acceptance of the
                        updated Privacy Policy.
                    </p>
                </Section>

                <Section title="15. How to Delete Your Account">
                    <p>You may request deletion of your OrderInOne account at any time by contacting us at:</p>
                    <p>jan@popboxtech.com</p>
                    <p>
                        Please include the email address associated with your OrderInOne account so that we can verify
                        your identity and process your request.
                    </p>
                    <p>
                        Once your identity has been verified, we will permanently delete your customer profile and
                        associated personal information, unless we are required to retain certain information for
                        legal, security, fraud prevention, or legitimate business purposes.
                    </p>
                    <p>
                        Please note that while your account and personal profile information will be deleted,
                        historical order records may be retained indefinitely for operational history, reporting,
                        customer support, auditing, and business record-keeping.
                    </p>
                    <p>After your account has been deleted, you will no longer be able to access OrderInOne using that account.</p>
                </Section>

                <Section title="16. Contact Us">
                    <p>If you have any questions about this Privacy Policy or how your personal information is handled, please contact us:</p>
                    <p>Popbox Pty Ltd</p>
                    <p>Email: jan@popboxtech.com</p>
                    <p>South Africa</p>
                </Section>
            </div>
        </main>
    );
}
